import React, { ComponentProps, useCallback, useState } from 'react'

import { removeOneWidgetWindow } from '@/entities/widgetWindows'
import { updateActiveWidgetWindowId } from '@/entities/widgetWindows/widgetWindowsSlice'
import { MosaicPath, MosaicWindow } from '@/shared/components/Mosaic'
import { isToolWidgetType } from '@/shared/components/Mosaic/lib/isToolWidgetType'
import { WidgetType, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { styled } from '@mui/material'

import { useWidgetToolbarContext } from '../WidgetToolbar/WidgetToolbarContext'
import { defaultWidgetNames } from './libs/defaultWidgetNames'
import { useMosaicLayoutContext } from './MosaicLayoutContext'
import { MosaicWindowToolbar } from './MosaicWindowToolbar'

export type MosaicWindowWidgetProps = Pick<ComponentProps<typeof MosaicWindow<WidgetType>>, 'onDropCreate' | 'draggable' | 'CustomWindowContext'> & {
  widgetId: WidgetTypeWithSuffix
  widgetType: WidgetType
  path: MosaicPath
  children: React.ReactNode
  isAdditionalToolbarEnabled?: boolean
  additionalToolbarElement?: React.ReactNode
  toolbarActions?: React.ReactNode
}

export const MosaicWindowWidget: React.FC<MosaicWindowWidgetProps> = ({
  widgetId, widgetType, path, children,
  isAdditionalToolbarEnabled, additionalToolbarElement, toolbarActions,
  CustomWindowContext, onDropCreate, ...props }) => {
  const [additionalControlsOpen, setAdditionalControlsOpen] = useState(false)
  const { closeWidgetWindows } = useMosaicLayoutContext()
  const { moveWidgetToToolbar } = useWidgetToolbarContext()
  const dispatch = useAppDispatch()
  const isFocused = useAppSelector(state => state.widgetWindows.activeWidgetWindowId === widgetId)

  const onDragEnd = (type: 'drop' | 'reset' | 'docked') => {
    if (type === 'docked') {
      closeWidgetWindows([widgetId])
    }
  }

  const onWindowClose = () => {
    // Dock the tool widget
    if (isToolWidgetType(widgetType)) {
      // Move the widget to the toolbar back to its original position
      moveWidgetToToolbar(widgetType, -1)
    }

    dispatch(removeOneWidgetWindow(widgetId))
    closeWidgetWindows([widgetId])
  }

  const onWindowClick = useCallback(() => {
    dispatch(updateActiveWidgetWindowId(widgetId))
  }, [dispatch, widgetId])

  return (
    <StyledWindow
      id={widgetId}
      title={defaultWidgetNames[widgetType]}
      widgetType={widgetType}
      path={path}
      onWindowClick={onWindowClick}
      $isFocused={isFocused}
      onDragStart={() => console.log('MosaicWindow.onDragStart')}
      onDragEnd={onDragEnd}
      onDropCreate={onDropCreate}
      additionalControls={additionalToolbarElement}
      CustomWindowContext={CustomWindowContext}
      disableAdditionalControlsOverlay
      onAdditionalControlsToggle={(toggle) => {
        setAdditionalControlsOpen(toggle)
      }}
      renderToolbar={(toolbarProps) => (
        <div style={{ width: '100%', height: '100%' }}>
          <MosaicWindowToolbar
            isAdditionalToolbarEnabled={isAdditionalToolbarEnabled}
            additionalControlsOpen={additionalControlsOpen}
            toolbarActions={toolbarActions}
            onWindowClose={onWindowClose}
            {...toolbarProps}
          />
        </div>
      )}
      {...props}
    >
      <StyledWidgetPlaceholder>
        {children}
      </StyledWidgetPlaceholder>
    </StyledWindow>
  )
}

const StyledWindow = styled(MosaicWindow<WidgetType>, {
  shouldForwardProp: (prop) => prop !== '$isFocused',
}) <{ $isFocused?: boolean }>`
  position: relative;

  ${({ $isFocused, theme }) => $isFocused && `
    outline: 1px solid ${theme.palette.primary.main};
  `}
`

const StyledWidgetPlaceholder = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.palette.custom.widget.backgroundColor};
  color: ${props => props.theme.palette.text.secondary};
  outline: 1px solid ${props => props.theme.palette.primary.main};
`

